import React, { useMemo, useState } from 'react';
import { CheckCircleIcon, Edit3Icon, ShieldCheckIcon, FileTextIcon, ImageIcon, FileIcon, DownloadIcon, EyeIcon, TrashIcon, UploadIcon, CreditCardIcon, AlertTriangleIcon, ClockIcon, CalendarIcon, UsersIcon } from 'lucide-react';
import { InvoiceDetails, InvoiceDocument, formatAmount } from './documentReviewTypes';
import { VendorSelector } from '../vendor/VendorSelector';
import { allVendors } from '../vendor/sharedVendorData';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
import { BusinessHistory, BusinessEvent } from '../common/BusinessHistory';
import { RegulatoryAuditTrail, AuditEntry } from '../common/RegulatoryAuditTrail';
interface DocumentDataPanelProps {
  details: InvoiceDetails;
  document: InvoiceDocument;
  isEditAllMode?: boolean;
  onUpdateDocument?: (updates: Partial<InvoiceDocument>) => void;
}
// Mock historical notes
const mockPreviousNotes = [{
  id: 'note-1',
  author: 'Sarah Chen',
  date: '15 Jan 2024 2:30 PM',
  visibility: 'Internal' as NoteVisibility,
  text: 'Verified line items against PO. All quantities match.'
}, {
  id: 'note-2',
  author: 'Mike Johnson',
  date: '14 Jan 2024 10:15 AM',
  visibility: 'Internal' as NoteVisibility,
  text: 'Received from vendor via email. Forwarded to AP team.'
}];
// Mock attachments data
const mockAttachments = [{
  id: 'att-1',
  name: 'Original_Invoice.pdf',
  type: 'pdf',
  size: 1250000,
  uploadDate: '2024-01-15',
  uploadedBy: 'System (Email)',
  status: 'Active'
}, {
  id: 'att-2',
  name: 'PO_Confirmation.pdf',
  type: 'pdf',
  size: 890000,
  uploadDate: '2024-01-10',
  uploadedBy: 'Sarah Chen',
  status: 'Active'
}, {
  id: 'att-3',
  name: 'Delivery_Receipt.jpg',
  type: 'jpg',
  size: 2340000,
  uploadDate: '2024-01-14',
  uploadedBy: 'Warehouse Team',
  status: 'Active'
}];
// Helper functions
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
function getFileIcon(type: string) {
  switch (type.toLowerCase()) {
    case 'pdf':
      return <FileTextIcon className="w-5 h-5 text-red-500" data-id="element-4646" />;
    case 'docx':
    case 'doc':
      return <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-4647" />;
    case 'xlsx':
    case 'xls':
      return <FileTextIcon className="w-5 h-5 text-green-500" data-id="element-4648" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <ImageIcon className="w-5 h-5 text-purple-500" data-id="element-4649" />;
    default:
      return <FileIcon className="w-5 h-5 text-gray-500" data-id="element-4650" />;
  }
}
function getVisibilityIcon(visibility: NoteVisibility) {
  switch (visibility) {
    case 'Public':
      return <span className="inline-flex items-center text-green-600" data-id="element-4651">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4652" />
          Public
        </span>;
    case 'Internal':
      return <span className="inline-flex items-center text-blue-600" data-id="element-4653">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4654" />
          Internal
        </span>;
    case 'Creator-only':
      return <span className="inline-flex items-center text-red-600" data-id="element-4655">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4656" />
          Private
        </span>;
    default:
      return <span className="inline-flex items-center text-gray-600" data-id="element-4657">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4658" />
          {visibility}
        </span>;
  }
}
function getComplianceStatusBadge(status: string) {
  switch (status) {
    case 'Compliant':
    case 'Verified':
    case 'Valid':
    case 'Clear':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4659">
          <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-4660" />
          {status}
        </span>;
    case 'Pending Review':
    case 'Not Verified':
    case 'Pending':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-4661">
          <ClockIcon className="w-3 h-3 mr-1" data-id="element-4662" />
          {status}
        </span>;
    case 'Non-Compliant':
    case 'Failed':
    case 'Expired':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-4663">
          <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-4664" />
          {status}
        </span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4665">
          {status}
        </span>;
  }
}
export function DocumentDataPanel({
  details,
  document,
  isEditAllMode = false,
  onUpdateDocument
}: DocumentDataPanelProps) {
  const [activeTab, setActiveTab] = useState('details');
  // Notes state
  const [noteText, setNoteText] = useState(document.notes || '');
  const [noteVisibility, setNoteVisibility] = useState<NoteVisibility>('Internal');
  // Find current vendor ID based on supplier name
  const currentVendor = allVendors.find(v => v.name === document.supplier);
  const currentVendorId = currentVendor ? currentVendor.id : null;
  const handleDocumentChange = (field: keyof InvoiceDocument, value: any) => {
    if (onUpdateDocument) {
      onUpdateDocument({
        [field]: value
      });
    }
  };
  const handleNumberChange = (field: keyof InvoiceDocument, value: string) => {
    if (onUpdateDocument) {
      const numValue = parseFloat(value);
      onUpdateDocument({
        [field]: isNaN(numValue) ? 0 : numValue
      });
    }
  };
  const handleVendorChange = (vendorId: string | null) => {
    if (vendorId && onUpdateDocument) {
      const vendor = allVendors.find(v => v.id === vendorId);
      if (vendor) {
        onUpdateDocument({
          supplier: vendor.name,
          supplierABN: vendor.taxId || ''
        });
      }
    }
  };
  // Build vendor verification data
  const vendorVerification = {
    complianceStatus: details.vendorBankCheck ? 'Compliant' : 'Pending Review',
    bankVerification: {
      status: details.vendorBankCheck ? 'Verified' : 'Not Verified',
      lastChecked: details.lastVerifiedDate || null,
      accountName: details.vendorName,
      issues: details.vendorBankCheck ? [] : [{
        severity: 'High',
        description: 'Bank account details not yet verified'
      }]
    },
    documents: [{
      id: 1,
      name: 'Business Registration',
      type: 'Legal',
      uploadDate: '2023-06-15',
      expiryDate: null,
      status: 'Valid'
    }, {
      id: 2,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-06-15',
      expiryDate: '2024-06-15',
      status: 'Valid'
    }, {
      id: 3,
      name: 'Tax Compliance Certificate',
      type: 'Tax',
      uploadDate: '2023-09-01',
      expiryDate: '2024-09-01',
      status: 'Valid'
    }],
    sanctionsCheck: {
      status: 'Clear',
      lastChecked: details.lastVerifiedDate || 'N/A',
      issues: []
    }
  };
  const tabs = [{
    id: 'details',
    label: 'Details'
  }, {
    id: 'notes',
    label: 'Notes'
  }, {
    id: 'attachments',
    label: 'Attachments'
  }, {
    id: 'verification',
    label: 'Vendor Verification'
  }, {
    id: 'history',
    label: 'History'
  }];
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-full" data-id="element-4666">
      <div className="px-6 pt-4 border-b border-gray-200 flex-shrink-0" data-id="element-4667">
        <div className="flex gap-1" data-id="element-4668">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === tab.id ? 'bg-white border border-b-white border-gray-200 text-indigo-700 -mb-px' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4669">
              {tab.label}
            </button>)}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 min-h-0" data-id="element-4670">
        {activeTab === 'details' && <div className="space-y-4" data-id="element-4671">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-4672">
              <div className="w-full max-w-md" data-id="element-4673">
                <div className="text-xs text-gray-500 mb-1" data-id="element-4674">Vendor</div>
                {isEditAllMode ? <VendorSelector value={currentVendorId} onChange={handleVendorChange} placeholder="Search and select vendor..." data-id="element-4675" /> : <div className="text-lg font-semibold text-gray-900" data-id="element-4676">
                    {details.vendorName}
                  </div>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-4677">
              <div className="space-y-3" data-id="element-4678">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4679">
                  Vendor
                </div>
                <div data-id="element-4680">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4681">
                    Supplier Code:
                  </label>
                  <div className="text-sm" data-id="element-4682">{details.supplierCode}</div>
                </div>
                <div data-id="element-4683">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4684">
                    Tax Number:
                  </label>
                  <div className="text-sm" data-id="element-4685">{details.vendorTaxNo}</div>
                </div>
                <div data-id="element-4686">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4687">
                    Email:
                  </label>
                  <div className="text-sm" data-id="element-4688">{details.email}</div>
                </div>
                <div data-id="element-4689">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4690">
                    Phone:
                  </label>
                  <div className="text-sm" data-id="element-4691">{details.phoneNumber || '-'}</div>
                </div>
                {details.vendorBankCheck !== undefined && <div data-id="element-4692">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4693">
                      Bank Check:
                    </label>
                    <div className="flex items-center" data-id="element-4694">
                      {details.vendorBankCheck ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-4695" /> : <span className="text-sm text-gray-400" data-id="element-4696">
                          Not verified
                        </span>}
                    </div>
                  </div>}
              </div>
              <div className="space-y-3" data-id="element-4697">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4698">
                  Entity
                </div>
                <div data-id="element-4699">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4700">
                    Entity:
                  </label>
                  <div className="text-sm" data-id="element-4701">{details.entity}</div>
                </div>
                <div data-id="element-4702">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4703">
                    Site:
                  </label>
                  <div className="text-sm" data-id="element-4704">{details.site}</div>
                </div>
                {details.paymentTerms && <div data-id="element-4705">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4706">
                      Payment Terms:
                    </label>
                    <div className="text-sm" data-id="element-4707">{details.paymentTerms}</div>
                  </div>}
              </div>
              <div className="space-y-3" data-id="element-4708">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4709">
                  Address
                </div>
                <div data-id="element-4710">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4711">
                    Address:
                  </label>
                  <div className="text-sm" data-id="element-4712">{details.address}</div>
                </div>
                <div data-id="element-4713">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4714">
                    City:
                  </label>
                  <div className="text-sm" data-id="element-4715">{details.city}</div>
                </div>
                <div data-id="element-4716">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4717">
                    State:
                  </label>
                  <div className="text-sm" data-id="element-4718">{details.state}</div>
                </div>
                <div data-id="element-4719">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4720">
                    Postcode:
                  </label>
                  <div className="text-sm" data-id="element-4721">{details.postcode}</div>
                </div>
                <div data-id="element-4722">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4723">
                    Country:
                  </label>
                  <div className="text-sm" data-id="element-4724">{details.country}</div>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200" data-id="element-4725">
              <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-4726">
                Document Header
              </h3>
              <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-4727">
                <div className="space-y-3" data-id="element-4728">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4729">
                    Document
                  </div>
                  <div data-id="element-4730">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4731">
                      Number:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.invoiceNumber} onChange={e => handleDocumentChange('invoiceNumber', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4732" /> : <div className="text-sm" data-id="element-4733">{document.invoiceNumber}</div>}
                  </div>
                  <div data-id="element-4734">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4735">
                      Currency:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.currency} onChange={e => handleDocumentChange('currency', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4736" /> : <div className="text-sm" data-id="element-4737">{document.currency}</div>}
                  </div>
                  {(document.poNumber || isEditAllMode) && <div data-id="element-4738">
                      <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4739">
                        PO Number:
                      </label>
                      {isEditAllMode ? <input type="text" value={document.poNumber || ''} onChange={e => handleDocumentChange('poNumber', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Optional" data-id="element-4740" /> : <div className="text-sm font-medium text-blue-600" data-id="element-4741">
                          {document.poNumber}
                        </div>}
                    </div>}
                </div>
                <div className="space-y-3" data-id="element-4742">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4743">
                    Dates
                  </div>
                  <div data-id="element-4744">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4745">
                      Document Date:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.invoiceDate} onChange={e => handleDocumentChange('invoiceDate', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4746" /> : <div className="text-sm" data-id="element-4747">{document.invoiceDate}</div>}
                  </div>
                  <div data-id="element-4748">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4749">
                      Due Date:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.dueDate} onChange={e => handleDocumentChange('dueDate', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4750" /> : <div className="text-sm" data-id="element-4751">{document.dueDate}</div>}
                  </div>
                  {(document.receivedDate || isEditAllMode) && <div data-id="element-4752">
                      <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4753">
                        Received:
                      </label>
                      {isEditAllMode ? <input type="text" value={document.receivedDate || ''} onChange={e => handleDocumentChange('receivedDate', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Optional" data-id="element-4754" /> : <div className="text-sm" data-id="element-4755">{document.receivedDate}</div>}
                    </div>}
                </div>
                <div className="space-y-3" data-id="element-4756">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4757">
                    Amounts
                  </div>
                  <div data-id="element-4758">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4759">
                      Subtotal:
                    </label>
                    {isEditAllMode ? <input type="number" step="0.01" value={document.subtotal} onChange={e => handleNumberChange('subtotal', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4760" /> : <div className="text-sm" data-id="element-4761">
                        {formatAmount(document.subtotal, document.currency)}
                      </div>}
                  </div>
                  <div data-id="element-4762">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4763">
                      Tax:
                    </label>
                    {isEditAllMode ? <input type="number" step="0.01" value={document.taxAmt} onChange={e => handleNumberChange('taxAmt', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4764" /> : <div className="text-sm" data-id="element-4765">
                        {formatAmount(document.taxAmt, document.currency)}
                      </div>}
                  </div>
                  <div data-id="element-4766">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4767">
                      Total:
                    </label>
                    {isEditAllMode ? <input type="number" step="0.01" value={document.total} onChange={e => handleNumberChange('total', e.target.value)} className="w-full px-2 py-1 text-sm font-semibold border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4768" /> : <div className="text-sm font-semibold" data-id="element-4769">
                        {formatAmount(document.total, document.currency)}
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* NOTES TAB */}
        {activeTab === 'notes' && <div className="space-y-6" data-id="element-4770">
            <StandardizedNotes value={noteText} onChange={setNoteText} visibility={noteVisibility} onVisibilityChange={setNoteVisibility} label="Invoice Notes" placeholder="Add notes about this document..." rows={4} data-id="element-4771" />

            {/* Previous Notes */}
            <div data-id="element-4772">
              <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-4773">
                Previous Notes
              </h4>
              <div className="space-y-3" data-id="element-4774">
                {mockPreviousNotes.map(note => <div key={note.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-4775">
                    <div className="flex items-center justify-between mb-2" data-id="element-4776">
                      <div className="flex items-center gap-2" data-id="element-4777">
                        <span className="text-sm font-medium text-gray-900" data-id="element-4778">
                          {note.author}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-4779">
                          {note.date}
                        </span>
                      </div>
                      <span className="text-xs" data-id="element-4780">
                        {getVisibilityIcon(note.visibility)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-4781">{note.text}</p>
                  </div>)}
              </div>
            </div>
          </div>}

        {/* ATTACHMENTS TAB */}
        {activeTab === 'attachments' && <div className="space-y-4" data-id="element-4782">
            <div className="flex items-center justify-between mb-2" data-id="element-4783">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-4784">
                Document Attachments
              </h4>
              <span className="text-xs text-gray-500" data-id="element-4785">
                {mockAttachments.length} files
              </span>
            </div>

            {/* Attachment List */}
            <div className="space-y-2" data-id="element-4786">
              {mockAttachments.map(attachment => <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4787">
                  <div className="flex items-center gap-3" data-id="element-4788">
                    {getFileIcon(attachment.type)}
                    <div data-id="element-4789">
                      <div className="text-sm font-medium text-gray-900" data-id="element-4790">
                        {attachment.name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-4791">
                        <span data-id="element-4792">{formatFileSize(attachment.size)}</span>
                        <span data-id="element-4793">•</span>
                        <span data-id="element-4794">{attachment.uploadedBy}</span>
                        <span data-id="element-4795">•</span>
                        <span data-id="element-4796">{attachment.uploadDate}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-4797">
                    <button className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors" title="Preview" data-id="element-4798">
                      <EyeIcon className="w-4 h-4" data-id="element-4799" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors" title="Download" data-id="element-4800">
                      <DownloadIcon className="w-4 h-4" data-id="element-4801" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete" data-id="element-4802">
                      <TrashIcon className="w-4 h-4" data-id="element-4803" />
                    </button>
                  </div>
                </div>)}
            </div>

            {/* Upload Area */}
            <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer" data-id="element-4804">
              <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-4805" />
              <p className="text-sm text-gray-600" data-id="element-4806">
                Drop files here or click to upload
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-4807">
                PDF, DOC, XLS, JPG up to 10MB
              </p>
            </div>
          </div>}

        {/* VENDOR VERIFICATION TAB */}
        {activeTab === 'verification' && <div className="space-y-4" data-id="element-4808">
            {/* Overall Compliance Status */}
            <div className={`rounded-lg p-4 flex items-start gap-3 ${vendorVerification.complianceStatus === 'Compliant' ? 'bg-green-50 border border-green-200' : vendorVerification.complianceStatus === 'Pending Review' ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`} data-id="element-4809">
              {vendorVerification.complianceStatus === 'Compliant' ? <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-4810" /> : vendorVerification.complianceStatus === 'Pending Review' ? <ClockIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-4811" /> : <AlertTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-4812" />}
              <div data-id="element-4813">
                <div className={`text-sm font-semibold mb-1 ${vendorVerification.complianceStatus === 'Compliant' ? 'text-green-900' : vendorVerification.complianceStatus === 'Pending Review' ? 'text-yellow-900' : 'text-red-900'}`} data-id="element-4814">
                  Vendor {vendorVerification.complianceStatus}
                </div>
                <div className={`text-xs ${vendorVerification.complianceStatus === 'Compliant' ? 'text-green-700' : vendorVerification.complianceStatus === 'Pending Review' ? 'text-yellow-700' : 'text-red-700'}`} data-id="element-4815">
                  Last verified: {details.lastVerifiedDate || 'N/A'}
                </div>
              </div>
            </div>

            {/* Bank Account Verification */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4816">
              <div className="flex items-center justify-between mb-3" data-id="element-4817">
                <div className="flex items-center gap-2" data-id="element-4818">
                  <CreditCardIcon className="w-4 h-4 text-gray-500" data-id="element-4819" />
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4820">
                    Bank Account Verification
                  </span>
                </div>
                {getComplianceStatusBadge(vendorVerification.bankVerification.status)}
              </div>
              {vendorVerification.bankVerification.lastChecked && <p className="text-xs text-gray-500 mb-2" data-id="element-4821">
                  Last checked:{' '}
                  {vendorVerification.bankVerification.lastChecked}
                </p>}
              {vendorVerification.bankVerification.issues.length > 0 && <div className="mt-2 space-y-1" data-id="element-4822">
                  {vendorVerification.bankVerification.issues.map((issue, idx) => <div key={idx} className={`text-xs flex items-start gap-1 ${issue.severity === 'High' ? 'text-red-600' : 'text-yellow-600'}`} data-id="element-4823">
                        <AlertTriangleIcon className="w-3 h-3 mt-0.5 flex-shrink-0" data-id="element-4824" />
                        {issue.description}
                      </div>)}
                </div>}
              <button className="mt-3 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors" data-id="element-4825">
                Verify Bank Details
              </button>
            </div>

            {/* Compliance Documents */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4826">
              <div className="flex items-center gap-2 mb-3" data-id="element-4827">
                <FileTextIcon className="w-4 h-4 text-gray-500" data-id="element-4828" />
                <span className="text-sm font-semibold text-gray-900" data-id="element-4829">
                  Compliance Documents
                </span>
              </div>
              <div className="space-y-2" data-id="element-4830">
                {vendorVerification.documents.map(doc => <div key={doc.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-4831">
                    <div data-id="element-4832">
                      <div className="text-sm text-gray-900" data-id="element-4833">{doc.name}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-4834">
                        <span data-id="element-4835">{doc.type}</span>
                        <span data-id="element-4836">•</span>
                        <span data-id="element-4837">Uploaded: {doc.uploadDate}</span>
                        {doc.expiryDate && <>
                            <span data-id="element-4838">•</span>
                            <span className="flex items-center gap-1" data-id="element-4839">
                              <CalendarIcon className="w-3 h-3" data-id="element-4840" />
                              Expires: {doc.expiryDate}
                            </span>
                          </>}
                      </div>
                    </div>
                    {getComplianceStatusBadge(doc.status)}
                  </div>)}
              </div>
            </div>

            {/* Sanctions & Watchlist */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4841">
              <div className="flex items-center justify-between mb-3" data-id="element-4842">
                <div className="flex items-center gap-2" data-id="element-4843">
                  <ShieldCheckIcon className="w-4 h-4 text-gray-500" data-id="element-4844" />
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4845">
                    Sanctions & Watchlist
                  </span>
                </div>
                {getComplianceStatusBadge(vendorVerification.sanctionsCheck.status)}
              </div>
              <p className="text-xs text-gray-500" data-id="element-4846">
                Last checked: {vendorVerification.sanctionsCheck.lastChecked}
              </p>
              {vendorVerification.sanctionsCheck.issues.length === 0 && <div className="mt-2 flex items-center gap-1 text-xs text-green-600" data-id="element-4847">
                  <CheckCircleIcon className="w-3 h-3" data-id="element-4848" />
                  No sanctions or watchlist matches found
                </div>}
            </div>
          </div>}

        {/* HISTORY TAB */}
        {activeTab === 'history' && <HistoryTabContent document={document} data-id="element-4849" />}
      </div>
    </div>;
}
function HistoryTabContent({
  document
}: {
  document: InvoiceDocument;
}) {
  const [subTab, setSubTab] = useState<'timeline' | 'audit'>('timeline');
  const {
    businessEvents,
    auditEntries
  } = useMemo(() => {
    const now = new Date();
    const receivedDate = new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000);
    const ocrDate = new Date(receivedDate.getTime() + 5 * 60 * 1000);
    const vendorMatchDate = new Date(ocrDate.getTime() + 2 * 60 * 1000);
    const validationDate = new Date(vendorMatchDate.getTime() + 1 * 60 * 1000);
    const matchingDate = new Date(validationDate.getTime() + 10 * 60 * 1000);
    const glCodingDate = new Date(matchingDate.getTime() + 15 * 60 * 1000);
    const approvalRequestDate = new Date(glCodingDate.getTime() + 5 * 60 * 1000);
    const approvalDate = new Date(approvalRequestDate.getTime() + 2 * 60 * 60 * 1000);
    const bEvents: BusinessEvent[] = [{
      id: 'evt-1',
      timestamp: receivedDate.toISOString(),
      description: 'Invoice received',
      actor: 'Email Ingestion Service',
      actorType: 'system',
      category: 'received',
      details: `Received via email from ${document.supplier.toLowerCase().replace(/[^a-z0-9]/g, '')}@example.com. File: ${document.invoiceNumber}.pdf`
    }, {
      id: 'evt-2',
      timestamp: ocrDate.toISOString(),
      description: 'OCR extraction completed',
      actor: 'Document Intelligence',
      actorType: 'system',
      category: 'processing',
      details: `Extracted ${8 + document.lineItems.length} fields with 96% average confidence. Total: ${document.currency} ${document.total.toLocaleString()}.`
    }, {
      id: 'evt-3',
      timestamp: vendorMatchDate.toISOString(),
      description: `Vendor matched to ${document.supplier}`,
      actor: 'Vendor Matching Service',
      actorType: 'system',
      category: 'processing',
      details: `Matched to vendor "${document.supplier}" with 99% confidence based on ABN and name.`
    }, {
      id: 'evt-4',
      timestamp: validationDate.toISOString(),
      description: 'Validation passed',
      actor: 'Validation Service',
      actorType: 'system',
      category: 'validation',
      details: `All mandatory fields validated. Tax of ${document.currency} ${document.taxAmt.toLocaleString()} verified. No duplicate detected.`
    }];
    if (document.poNumber) {
      bEvents.push({
        id: 'evt-5',
        timestamp: matchingDate.toISOString(),
        description: 'Three-way matching completed',
        actor: 'Matching Engine',
        actorType: 'system',
        category: 'matching',
        details: `Invoice matched against PO ${document.poNumber}. ${document.lineItems.length} line(s) matched. Variance within tolerance.`
      });
    }
    bEvents.push({
      id: 'evt-6',
      timestamp: glCodingDate.toISOString(),
      description: 'GL coding applied',
      actor: 'GL Coding Engine',
      actorType: 'system',
      category: 'processing',
      details: `Default GL codes applied to ${document.lineItems.length} line item(s) based on vendor and category rules.`
    }, {
      id: 'evt-7',
      timestamp: approvalRequestDate.toISOString(),
      description: 'Approval requested',
      actor: 'Workflow Engine',
      actorType: 'system',
      category: 'approval',
      details: `Routed for approval based on amount threshold (${document.currency} ${document.total.toLocaleString()}).`
    });
    const aEntries: AuditEntry[] = [{
      id: 'aud-1',
      timestamp: receivedDate.toISOString(),
      actor: 'Email Ingestion Service',
      actorType: 'system',
      category: 'integration',
      integrationName: 'Email Gateway',
      requestId: `MSG-${receivedDate.getTime()}`,
      responseStatus: 'Received',
      direction: 'inbound'
    } as AuditEntry, {
      id: 'aud-2',
      timestamp: receivedDate.toISOString(),
      actor: 'System',
      actorType: 'system',
      category: 'action',
      actionType: 'DOCUMENT_CREATED',
      targetEntity: document.invoiceNumber,
      description: 'Invoice document created from email attachment'
    } as AuditEntry, {
      id: 'aud-3',
      timestamp: ocrDate.toISOString(),
      actor: 'Workflow Engine',
      actorType: 'system',
      category: 'workflow_transition',
      previousState: 'Received',
      newState: 'Processing',
      triggeredBy: 'Document Created'
    } as AuditEntry, {
      id: 'aud-4',
      timestamp: vendorMatchDate.toISOString(),
      actor: 'Vendor Matching Service',
      actorType: 'system',
      category: 'field_change',
      fieldName: 'Vendor',
      oldValue: '(unassigned)',
      newValue: document.supplier
    } as AuditEntry, {
      id: 'aud-5',
      timestamp: validationDate.toISOString(),
      actor: 'Validation Service',
      actorType: 'system',
      category: 'rule_evaluation',
      ruleId: 'RULE-DUPLICATE-001',
      ruleVersion: '1.0.0',
      result: 'passed',
      details: `No duplicate for ${document.invoiceNumber} from ${document.supplier}`
    } as AuditEntry, {
      id: 'aud-6',
      timestamp: validationDate.toISOString(),
      actor: 'Validation Service',
      actorType: 'system',
      category: 'rule_evaluation',
      ruleId: 'RULE-TAX-CALC-001',
      ruleVersion: '1.2.0',
      result: 'passed',
      details: `Tax amount ${document.currency} ${document.taxAmt.toLocaleString()} verified against line items`
    } as AuditEntry];
    if (document.poNumber) {
      aEntries.push({
        id: 'aud-7',
        timestamp: matchingDate.toISOString(),
        actor: 'Matching Engine',
        actorType: 'system',
        category: 'rule_evaluation',
        ruleId: 'RULE-PO-MATCH-001',
        ruleVersion: '2.1.0',
        result: 'passed',
        details: `Invoice matched to PO ${document.poNumber}. Amount variance within 2% tolerance.`
      } as AuditEntry);
    }
    aEntries.push({
      id: 'aud-8',
      timestamp: approvalRequestDate.toISOString(),
      actor: 'Workflow Engine',
      actorType: 'system',
      category: 'workflow_transition',
      previousState: 'Validated',
      newState: 'Pending Approval',
      triggeredBy: 'Validation Completed'
    } as AuditEntry);
    return {
      businessEvents: bEvents,
      auditEntries: aEntries
    };
  }, [document]);
  const lastActivity = businessEvents.length > 0 ? new Date(Math.max(...businessEvents.map(e => new Date(e.timestamp).getTime()))).toLocaleString() : 'N/A';
  return <div className="space-y-4" data-id="element-4850">
      {/* Summary Bar */}
      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex items-center gap-6" data-id="element-4851">
        <div data-id="element-4852">
          <div className="text-xs text-gray-500" data-id="element-4853">Events</div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-4854">
            {businessEvents.length + auditEntries.length}
          </div>
        </div>
        <div className="w-px h-8 bg-gray-200" data-id="element-4855" />
        <div data-id="element-4856">
          <div className="text-xs text-gray-500" data-id="element-4857">Last Activity</div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-4858">
            {lastActivity}
          </div>
        </div>
        <div className="w-px h-8 bg-gray-200" data-id="element-4859" />
        <div data-id="element-4860">
          <div className="text-xs text-gray-500" data-id="element-4861">Status</div>
          <div className="text-sm font-semibold text-amber-600" data-id="element-4862">
            Pending Review
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-id="element-4863">
        <button onClick={() => setSubTab('timeline')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${subTab === 'timeline' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4864">
          Timeline
        </button>
        <button onClick={() => setSubTab('audit')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${subTab === 'audit' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4865">
          Audit Trail
        </button>
      </div>

      {/* Content */}
      {subTab === 'timeline' ? <BusinessHistory entityType="Invoice" entityId={document.invoiceNumber} events={businessEvents} data-id="element-4866" /> : <RegulatoryAuditTrail entityType="Invoice" entityId={document.invoiceNumber} auditEntries={auditEntries} hasAuditAccess={true} data-id="element-4867" />}
    </div>;
}